import React, { useState, useEffect } from 'react';
import { parseISO, isValid, format } from 'date-fns';
import {
  Alert,
  Box,
  Card,
  Chip,
  Grid,
  Icon,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import { useUser } from 'components/RDAuthContext';
import { useAdminContext } from 'context/AdminContext';
import { hierarchyService } from '../services/domain'; // UPDATED: Using hierarchyService
import { useHierarchyStructure } from '../hooks/useLocationAdminData';
import { useQueryClient } from '@tanstack/react-query';

function HierarchyLevelRow({ level, isEditMode, onLabelChange, onDelete, isFixed }) {
  const { id, name, label, isActive, order, icon, levelId } = level;
  
  const getLevelDisplay = () => {
    if (levelId === 'LOCATION' || id === 'LOCATION') return 'Base Level';
    if (levelId === 'BRAND' || id === 'BRAND') return 'Top Level';
    if (levelId && levelId.startsWith('ORGLEVEL_')) {
      const levelNum = parseInt(levelId.replace('ORGLEVEL_', ''));
      return `Level ${levelNum}`;
    }
    if (id && id.startsWith('ORGLEVEL_')) {
      return `Level ${order}`;
    }
    return name;
  };

  return (
    <Card sx={{ mb: 2 }}>
      <Box p={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <Icon sx={{ mr: 1.5, color: '#7C3AED' }}>
              {icon || (levelId === 'BRAND' || id === 'BRAND' ? 'business' : 
                       levelId === 'LOCATION' || id === 'LOCATION' ? 'store' : 'hub')}
            </Icon>
            <Box>
              {isEditMode && !isFixed ? (
                <TextField
                  value={label === 'Store' ? 'Location' : (label || name)}
                  onChange={(e) => onLabelChange(id || levelId, e.target.value)}
                  size="small"
                  variant="standard"
                  sx={{ 
                    '& .MuiInput-input': {
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      padding: '2px 0'
                    }
                  }}
                />
              ) : (
                <Typography variant="button" fontWeight="medium">
                  {label === 'Store' ? 'Location' : (label || name)}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block">
                {getLevelDisplay()}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={2}>
            <Chip 
              label="Active" 
              size="small" 
              color="success"
              variant="outlined"
            />
            
            {isEditMode && !isFixed && (
              <IconButton 
                size="small" 
                color="error"
                onClick={() => onDelete(id || levelId)}
              >
                <Icon>delete</Icon>
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

function StructureTab() {
  const { user } = useUser();
  const { selectedClient } = useAdminContext();
  const queryClient = useQueryClient();
  const [isEditMode, setIsEditMode] = useState(false);
  const [hierarchyLevels, setHierarchyLevels] = useState([]);
  const [editedLevels, setEditedLevels] = useState([]);

  console.log('🎯 StructureTab - selectedClient:', selectedClient, 'user.client:', user?.client);
  const [changeReason, setChangeReason] = useState('');
  const [currentVersion, setCurrentVersion] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch hierarchy data using the hook
  const { data: hierarchyData, isLoading, refetch } = useHierarchyStructure();

  // Update local state when data changes
  useEffect(() => {
    if (hierarchyData?.structure && hierarchyData.structure.length > 0) {
      const validatedData = hierarchyData.structure.filter(level => level && (level.levelId || level.id));
      setHierarchyLevels(validatedData);
      setEditedLevels(validatedData);
    } else {
      // No default structure - empty if none exists
      setHierarchyLevels([]);
      setEditedLevels([]);
    }
  }, [hierarchyData?.structure]);

  useEffect(() => {
    if (hierarchyData?.version) {
      setCurrentVersion(hierarchyData.version);
    } else {
      setCurrentVersion(null);
    }
  }, [hierarchyData?.version]);

  const handleEditToggle = () => {
    if (isEditMode) {
      // Cancel edit mode
      setEditedLevels(hierarchyLevels);
      setChangeReason('');
      setHasChanges(false);
    } else {
      // Entering edit mode - create a working copy
      if (hierarchyLevels.length === 0) {
        // If no structure exists, create initial structure
        const initialStructure = [
          { levelId: 'BRAND', name: 'Brand', label: 'Brand', isActive: true, isUsed: true, order: 999, icon: 'business' },
          { levelId: 'ORGLEVEL_3', name: 'Level 3', label: 'Region', isActive: true, isUsed: true, order: 3, icon: 'hub' },
          { levelId: 'ORGLEVEL_2', name: 'Level 2', label: 'Area', isActive: true, isUsed: true, order: 2, icon: 'account_tree' },
          { levelId: 'ORGLEVEL_1', name: 'Level 1', label: 'Market', isActive: true, isUsed: true, order: 1, icon: 'store_mall_directory' },
          { levelId: 'LOCATION', name: 'Location', label: 'Location', isActive: true, isUsed: true, order: 0, icon: 'store' }
        ];
        setEditedLevels(initialStructure);
      } else {
        // Create working copy of existing structure
        setEditedLevels([...hierarchyLevels]);
      }
    }
    setIsEditMode(!isEditMode);
  };

  const handleLabelChange = (levelId, newLabel) => {
    setEditedLevels(prev => 
      prev.map(level => 
        (level.levelId === levelId || level.id === levelId) ? { ...level, label: newLabel } : level
      )
    );
    setHasChanges(true);
  };

  const handleAddLevel = () => {
    const existingOrgLevels = editedLevels.filter(l => 
      (l.levelId && l.levelId.startsWith('ORGLEVEL_')) || 
      (l.id && l.id.startsWith('ORGLEVEL_'))
    );
    const currentCount = existingOrgLevels.length;
    
    if (currentCount >= 3) {
      alert('Maximum 3 editable levels allowed between Location and Brand');
      return;
    }

    // New level gets the next highest number
    const newLevelNumber = currentCount + 1;
    const newLevel = {
      levelId: `ORGLEVEL_${newLevelNumber}`,
      name: `Level ${newLevelNumber}`,
      label: `New Level`,
      isActive: true,
      isUsed: true,
      order: newLevelNumber,
      icon: 'hub'
    };

    // Insert the new level after Brand
    const brandIndex = editedLevels.findIndex(l => l.levelId === 'BRAND' || l.id === 'BRAND');
    const newLevels = [...editedLevels];
    newLevels.splice(brandIndex + 1, 0, newLevel);
    
    setEditedLevels(newLevels);
    setHasChanges(true);
  };

  const handleDeleteLevel = (levelIdToDelete) => {
    setEditedLevels(prev => {
      // Remove the level entirely and adjust order numbers
      const updatedLevels = prev.filter(level => {
        const currentLevelId = level.levelId || level.id;
        return currentLevelId !== levelIdToDelete;
      });
      
      // Re-number the remaining org levels while maintaining the hierarchy
      // Collect and sort org levels by their current order (descending)
      const orgLevels = updatedLevels
        .filter(l => {
          const id = l.levelId || l.id;
          return id && id.startsWith('ORGLEVEL_');
        })
        .sort((a, b) => (b.order || 0) - (a.order || 0));
      
      // Renumber them starting from the highest
      let nextNumber = orgLevels.length;
      
      const renumberedLevels = updatedLevels.map(level => {
        const levelId = level.levelId || level.id;
        
        if (levelId && levelId.startsWith('ORGLEVEL_')) {
          // Find this level's position in the sorted array
          const index = orgLevels.findIndex(l => (l.levelId || l.id) === levelId);
          const newNumber = nextNumber - index;
          
          return {
            ...level,
            levelId: `ORGLEVEL_${newNumber}`,
            order: newNumber,
            name: `Level ${newNumber}`
          };
        }
        
        return level;
      });
      
      return renumberedLevels;
    });
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!changeReason.trim()) {
      alert('Please provide a reason for the change');
      return;
    }

    try {
      await hierarchyService.updateHierarchyStructure(
        selectedClient,
        editedLevels,
        changeReason,
        user?.id || user?.userName,
        `${user?.firstName} ${user?.lastName}`.trim() || user?.userName
      );

      setIsEditMode(false);
      setChangeReason('');
      setHasChanges(false);

      // Invalidate all relevant caches to ensure UI updates

      await queryClient.invalidateQueries({ queryKey: ['hierarchyStructure', selectedClient] });
      await queryClient.invalidateQueries({ queryKey: ['locationAdmin', selectedClient] });  // This updates Total Units tile
      await queryClient.invalidateQueries({ queryKey: ['orgUnitsData', selectedClient] });  // Invalidate org units data too
      
      // Force refetch
      await refetch();

    } catch (error) {
      console.error('Error saving hierarchy structure:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  const getEffectiveDateRange = () => {
    if (!currentVersion) return '';
    
    // Format dates to YYYY-MM-DD
    const formatDate = (dateStr) => {
      if (!dateStr) return 'Present';
      if (dateStr === 'Present') return dateStr;
      
      try {
        // Parse various date formats
        const date = parseISO(dateStr);
        if (isValid(date)) {
          return format(date, 'yyyy-MM-dd'); // Returns YYYY-MM-DD
        }
      } catch (e) {
        console.error('Error formatting date:', e);
      }
      return dateStr; // Return original if can't parse
    };
    
    const effectiveDate = formatDate(currentVersion.effectiveDate);
    const endDate = currentVersion.endDate ? formatDate(currentVersion.endDate) : 'Present';
    
    return `${effectiveDate} - ${endDate}`;
  };

  const sortedLevels = [...editedLevels].sort((a, b) => {
    const aId = a.levelId || a.id;
    const bId = b.levelId || b.id;
    
    // Ensure both items have an id
    if (!aId || !bId) return 0;
    
    // Brand always first
    if (aId === 'BRAND') return -1;
    if (bId === 'BRAND') return 1;
    
    // Location always last
    if (aId === 'LOCATION') return 1;
    if (bId === 'LOCATION') return -1;
    
    // Sort org levels by order (descending - higher numbers first)
    return (b.order || 0) - (a.order || 0);
  });

  const editableCount = editedLevels.filter(l => {
    const levelId = l.levelId || l.id;
    return levelId && levelId !== 'BRAND' && levelId !== 'LOCATION';
  }).length;

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography>Loading hierarchy structure...</Typography>
      </Box>
    );
  }

  // If no hierarchy exists and not in edit mode, show message
  if (hierarchyLevels.length === 0 && !isEditMode) {
    return (
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <Box p={3}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                  <Box>
                    <Typography variant="h5" fontWeight="medium">
                      Hierarchy Structure Definition
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                      No hierarchy structure has been defined yet
                    </Typography>
                  </Box>
                  <AdminButton 
                    variant="contained"
                    startIcon={<Icon>add</Icon>}
                    onClick={handleEditToggle}
                  >
                    Create Structure
                  </AdminButton>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <Box p={3}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
                <Box>
                  <Typography variant="h5" fontWeight="medium">
                    Hierarchy Structure Definition
                  </Typography>
                  {currentVersion && (
                    <Typography variant="caption" color="text.secondary" mt={1} display="block">
                      {getEffectiveDateRange()}
                    </Typography>
                  )}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {currentVersion && (
                    <Typography variant="caption" sx={{ mr: 2 }}>
                      Version {currentVersion.version}
                    </Typography>
                  )}
                  {isEditMode ? (
                    <>
                      <AdminButton 
                        variant="contained"
                        startIcon={<Icon>save</Icon>}
                        onClick={handleSaveChanges}
                        disabled={!hasChanges}
                      >
                        Save Changes
                      </AdminButton>
                      <AdminButton
                        variant="outlined"
                        startIcon={<Icon>close</Icon>}
                        onClick={handleEditToggle}
                      >
                        Cancel
                      </AdminButton>
                    </>
                  ) : (
                    <AdminButton 
                      variant="contained"
                      startIcon={<Icon>edit</Icon>}
                      onClick={handleEditToggle}
                    >
                      {hierarchyLevels.length === 0 ? 'Create Structure' : 'Edit Structure'}
                    </AdminButton>
                  )}
                </Box>
              </Box>

              {isEditMode && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Changes to the hierarchy structure will create a new version and affect all locations. Location and Brand levels are fixed.
                </Alert>
              )}

              {isEditMode && editableCount < 3 && (
                <Box mb={3} display="flex" justifyContent="center">
                  <AdminButton
                    variant="outlined"
                    startIcon={<Icon>add</Icon>}
                    onClick={handleAddLevel}
                    sx={{ 
                      borderStyle: 'dashed',
                      borderColor: 'text.secondary',
                      color: 'text.primary',
                      '&:hover': {
                        borderColor: 'text.primary',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    Add Level ({editableCount} of 3)
                  </AdminButton>
                </Box>
              )}

              <Box>
                {sortedLevels.map((level) => {
                  const levelId = level.levelId || level.id;
                  // Don't render deleted levels (those marked inactive during edit)
                  if (isEditMode && level.isDeleted) {
                    return null;
                  }
                  return (
                    <HierarchyLevelRow
                      key={levelId}
                      level={level}
                      isEditMode={isEditMode}
                      onLabelChange={handleLabelChange}
                      onDelete={handleDeleteLevel}
                      isFixed={levelId === 'BRAND' || levelId === 'LOCATION'}
                    />
                  );
                })}
              </Box>

              {isEditMode && (
                <Box mt={3}>
                  <Typography variant="body2" fontWeight="medium" mb={1}>
                    Reason for Change *
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="e.g., Adding district level for better territory management"
                    value={changeReason}
                    onChange={(e) => setChangeReason(e.target.value)}
                  />
                </Box>
              )}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default StructureTab;